import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

import {
  handleGetJoinedUsers,
  handleGetMatchResults,
  setMatchResultsSnapshot,
} from "../app/arenaSlice";
import { fetchUserInfo } from "../app/authSlice";
import http from "../helper/http-client";
import { debounce } from "../helper/helper";
import arenaXpSound from "../assets/audio/xp-sound.MP3";

const useArena = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [isStartLoading, setIsStartLoading] = useState(false);
  const [isOptionModal, setIsOptionModal] = useState(false);
  const [searchStr, setSearchStr] = useState("");
  const scrollPositionRef = useRef(0);
  const latestTopMatchIdRef = useRef(null);
  const arenaXpAudioRef = useRef(null);

  const {
    joinedUsers = [],
    isLoading,
    matchResults = [],
    totalPages,
  } = useSelector((state) => state.arena);
  const { user } = useSelector((state) => state.auth);

  const handleLoadMore = () => {
    if (page < totalPages) setPage((prev) => prev + 1);
  };

  const handleOptionModal = (flag) => {
    setIsOptionModal(flag);
  };

  const handleSearch = (e) => {
    setSearchStr(e.target.value);
  };

  const debouncedHandleSearch = debounce(handleSearch, 2000);

  const playArenaXpAudio = useCallback(() => {
    const arenaXpAudio = arenaXpAudioRef.current;
    if (!arenaXpAudio) return;
    arenaXpAudio.currentTime = 0;
    arenaXpAudio.play().catch(() => null);
  }, []);

  const unlockArenaXpAudio = useCallback(() => {
    const arenaXpAudio = arenaXpAudioRef.current;
    if (!arenaXpAudio) return;
    arenaXpAudio.muted = true;
    arenaXpAudio.currentTime = 0;
    arenaXpAudio
      .play()
      .then(() => {
        arenaXpAudio.pause();
        arenaXpAudio.currentTime = 0;
        arenaXpAudio.muted = false;
      })
      .catch(() => null);
  }, []);

  const isVisibleLoadMoreBtn = useMemo(() => {
    return totalPages > page;
  }, [totalPages, page]);

  const startArena = async (option) => {
    setIsStartLoading(true);
    unlockArenaXpAudio();
    try {
      const res = await http.post(
        `/arena/match/${"Sparring"}/${user.username}`,
        { option }
      );

      if (res.data) {
        if (option === "Manual") {
          dispatch(fetchUserInfo());
          navigate(`/arena/reply`, {
            state: {
              match: res.data,
              isReply: false,
              message: res?.data?.message,
            },
          });
        } else {
          dispatch(fetchUserInfo());
          return;
        }
      }
    } catch (err) {
      toast.error(err?.data?.message);
      console.error(err);
    } finally {
      setIsStartLoading(false);
    }
  };

  const handleRefreshArean = async () => {
    try {
      setPage(1);
      dispatch(fetchUserInfo());
      dispatch(
        handleGetMatchResults({ arenaTitle: "Sparring", page: 1, limit: 9 })
      );
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    dispatch(handleGetJoinedUsers("Sparring"));
    dispatch(fetchUserInfo());
  }, [dispatch]);

  useEffect(() => {
    scrollPositionRef.current = window.scrollY;

    dispatch(handleGetMatchResults({ arenaTitle: "Sparring", page, limit: 9, username: searchStr }));
  }, [dispatch, page, searchStr]);

  useEffect(() => {
    // Restore the scroll position after the data has been updated
    window.scrollTo({
      top: scrollPositionRef.current,
      behavior: "smooth",
    });
  }, [matchResults]);

  useEffect(() => {
    if (!arenaXpAudioRef.current) {
      const audio = new Audio(arenaXpSound);
      audio.preload = "auto";
      arenaXpAudioRef.current = audio;
    }
    return () => {
      if (arenaXpAudioRef.current) {
        arenaXpAudioRef.current.pause();
      }
    };
  }, []);

  useEffect(() => {
    if (matchResults?.length && !latestTopMatchIdRef.current) {
      latestTopMatchIdRef.current = matchResults[0]?._id || null;
    }
  }, [matchResults]);

  useEffect(() => {
    const pollArena = async () => {
      try {
        const res = await http.get(
          `/arena/get/results/Sparring?page=${page}&limit=9&username=${searchStr}`
        );
        const payload = res?.data || {};
        const incomingResults = payload?.matchResults || [];
        const topResult = incomingResults[0] || null;
        const topMatchId = topResult?._id || null;

        if (
          topMatchId &&
          latestTopMatchIdRef.current &&
          topMatchId !== latestTopMatchIdRef.current &&
          topResult?.winner === user?.username
        ) {
          playArenaXpAudio();
        }

        if (topMatchId) {
          latestTopMatchIdRef.current = topMatchId;
        }

        dispatch(setMatchResultsSnapshot(payload));
        dispatch(fetchUserInfo());
      } catch (err) {
        console.error(err);
      }
    };

    const timer = setInterval(pollArena, 5000);
    return () => clearInterval(timer);
  }, [dispatch, page, searchStr, user?.username, playArenaXpAudio]);

  return {
    joinedUsers,
    isLoading,
    matchResults,
    isStartLoading,
    totalPages,
    searchStr,
    isVisibleLoadMoreBtn,
    isOptionModal,
    handleLoadMore,
    startArena,
    handleRefreshArean,
    handleOptionModal,
    handleSearch: debouncedHandleSearch,
  };
};

export default useArena;
